<template>
  <div>
    <section class="campaign_filters mb_14">
      <div class="campaign_filter">
        <SearchInput
          v-model="searchValue"
          :placeholder="$t('AI.REPORT_COST.SEARCH_PLACEHOLDER')"
          @search="onSearch"
        />
      </div>
      <div class="campaign_filter filters__button-wrap">
        <div v-if="appliedFilters.length" class="filters__applied-indicator" />
        <woot-button
          class="margin-right-small clear"
          color-scheme="secondary"
          data-testid="create-new-contact"
          icon="filter"
          @click="onToggleFilter"
        >
          {{ $t('CONTACTS_PAGE.FILTER_CONTACTS') }}
        </woot-button>
      </div>
    </section>
    <div class="top-header-row">
      <div class="date-range-section">
        <label class="date-range-label">
          {{ $t('AI.REPORT_COST.DATE_RANGE.LABEL') }}
        </label>
        <div class="top-header-filter">
          <div class="date-range-selection">
            <woot-date-range-picker
              :value="customDateRange"
              :confirm-text="$t('AI.REPORT_COST.DATE_RANGE.CONFIRM')"
              :placeholder="$t('AI.REPORT_COST.DATE_RANGE.PLACEHOLDER')"
              @change="onCustomDateChange"
            />
          </div>
          <div class="grand-total-section">
            <div class="grand-total-label">
              {{ $t('AI.REPORT_COST.GRAND_TOTAL') }}:
            </div>
            <div class="grand-total-value">
              ${{ (grandTotal || 0).toFixed(2) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <CostsTable
      :cols="cols"
      :records="records"
      :is-loading="uiFlags.isFetching || false"
      :sort-config="sortConfig"
      :page-total="pageTotal"
      @on-sort-change="onSortChange"
    />

    <div class="loadMore_btn">
      <woot-button
        variant="clear"
        size="small"
        color-scheme="primary"
        :is-loading="uiFlags.isFetching || false"
        :is-disabled="paginationCompute.isLastPage"
        @click="onPageChange(Number(meta.current_page) + 1)"
      >
        {{ paginationCompute.btnText }}
      </woot-button>
    </div>

    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="onToggleFilter"
      size="medium"
    >
      <CostsAdvancedFilter
        v-if="showFiltersModal"
        :on-close="onToggleFilter"
        :filter-items="filterTypes"
        :initial-applied-filters="appliedFilters"
        :filter-attributes="filterAttributes"
        @applyFilter="onApplyFilter"
        @clearFilters="onClearFilters"
      />
    </woot-modal>
  </div>
</template>

<script>
import CostsTable from './CostsTable.vue';
import CostsAdvancedFilter from './CostsAdvancedFilter.vue';
import { mapGetters } from 'vuex';
import SearchInput from 'dashboard/components/ui/SearchInput.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import alertMixin from 'shared/mixins/alertMixin';
import helperMixin from 'shared/mixins/helperMixin';
import {
  reportCostFilterAttributList,
  reportCostFilterTypes,
} from '../ai.constant';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';

export default {
  components: {
    CostsTable,
    CostsAdvancedFilter,
    SearchInput,
    WootDateRangePicker,
  },
  mixins: [helperMixin, alertMixin],
  data() {
    return {
      searchValue: '',
      searchDebounceTimer: null,
      sortConfig: { name: 'asc' },
      showFiltersModal: false,
      appliedFilters: [],
      customDateRange: [new Date(), new Date()],
      cols: [
        { key: 'name', name: 'Name', visible: true, type: 'default' },
        { key: 'model', name: 'Model', visible: true, type: 'default' },
        { key: 'type', name: 'Type', visible: true, type: 'default' },
        {
          key: 'cost_per_call',
          name: 'Cost/Call',
          visible: true,
          type: 'default',
        },
        {
          key: 'total_calls',
          name: 'Total Calls',
          visible: true,
          type: 'default',
        },
        { key: 'status', name: 'Status', visible: true, type: 'default' },
        {
          key: 'last_used_at',
          name: 'Last Used',
          visible: true,
          type: 'default',
        },
        {
          key: 'total_cost',
          name: 'Total Cost',
          visible: true,
          type: 'default',
        },
      ],
    };
  },
  computed: {
    to() {
      return this.toCustomDate(this.customDateRange[1]);
    },
    from() {
      return this.fromCustomDate(this.customDateRange[0]);
    },
    ...mapGetters({
      uiFlags: 'reportCosts/getReportCostUIFlags',
      meta: 'reportCosts/getReportCostMeta',
      records: 'reportCosts/getReportCosts',
      grandTotal: 'reportCosts/getReportCostsTotal',
    }),
    pageTotal() {
      return this.records.reduce(
        (sum, record) => sum + (record.total_cost || 0),
        0
      );
    },
    filterTypes() {
      return reportCostFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(filter.attributeI18nKey),
      }));
    },
    filterAttributes() {
      return reportCostFilterAttributList.map(el => ({
        ...el,
        name: this.$t(el.name),
      }));
    },
    paginationCompute() {
      if (!this.meta) {
        return {
          isLastPage: true,
          btnText: this.$t('AI.REPORT_COST.LOAD_MORE'),
        };
      }
      const totalPages = Math.ceil(this.meta.count / this.meta.page_size);
      const isLastPage = this.meta.current_page >= totalPages;
      const currentCount = Math.min(
        this.meta.current_page * this.meta.page_size,
        this.meta.count
      );
      const btnText = `${this.$t(
        'AI.REPORT_COST.LOAD_MORE'
      )} (${currentCount}/${this.meta.count})`;

      return {
        isLastPage,
        btnText,
      };
    },
  },
  watch: {
    searchValue() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchRecordList({ page: 1 });
      }, 500);
    },
  },
  mounted() {
    this.fetchRecordList({ page: 1 });
  },
  methods: {
    fetchRecordList(query = {}) {
      try {
        const defaultQuery = {
          page: this.meta?.current_page,
          page_size: this.meta?.page_size,
          q: this.searchValue,
          sort: this.getSortAttribute(this.sortConfig),
          from: this.from,
          to: this.to,
          ...query,
        };

        if (this.appliedFilters?.length) {
          this.$store.dispatch(
            'reportCosts/filterReportCosts',
            this.cleanObject({
              ...defaultQuery,
              payload: this.appliedFilters,
            })
          );
        } else {
          this.$store.dispatch(
            'reportCosts/fetchReportCosts',
            this.cleanObject(defaultQuery)
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching cost records:', error);
      }
    },
    onPageChange(page) {
      this.fetchRecordList({ page: page || 1 });
    },
    onSearch() {
      // Search is now handled by the watcher with debounce
      // This method is kept for compatibility with the @search event
    },
    onToggleFilter() {
      this.showFiltersModal = !this.showFiltersModal;
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters;
      this.fetchRecordList({ page: 1 });
      this.showFiltersModal = false;
    },
    onClearFilters() {
      this.appliedFilters = [];
      this.fetchRecordList({ page: 1 });
      this.showFiltersModal = false;
    },
    getSortAttribute(newSort = {}) {
      let sortAttr = Object.keys({ ...this.sortConfig, ...newSort }).reduce(
        (acc, sortKey) => {
          const sortOrder = this.sortConfig[sortKey];
          if (sortOrder) {
            const sortOrderSign = sortOrder === 'asc' ? '' : '-';
            return `${sortOrderSign}${sortKey}`;
          }
          return acc;
        },
        ''
      );
      if (!sortAttr) {
        this.sortConfig = { name: 'asc' };
        sortAttr = 'name';
      }
      return sortAttr;
    },
    onSortChange(params) {
      let sortDetails = {};
      Object.keys(params).forEach(key => {
        if (params[key]) {
          sortDetails = {};
          sortDetails.key = key;
          sortDetails.order = params[key];
        }
      });

      this.sortConfig = params;
      const sort = this.getSortAttribute(sortDetails);
      this.fetchRecordList({ sort, page: 1 });
    },
    onCustomDateChange(value) {
      this.customDateRange = value;
      this.fetchRecordList({ page: 1 });
    },
    fromCustomDate(date) {
      return getUnixTime(startOfDay(date));
    },
    toCustomDate(date) {
      return getUnixTime(endOfDay(date));
    },
  },
};
</script>

<style scoped lang="scss">
.top-header {
  &-row {
    align-items: center;
    background: var(--white);
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 0;
  }

  &-filter {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
    width: 100%;
  }
}

.date-range-section {
  align-items: start;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  .date-range-label {
    color: var(--s-600);
    font-size: 12px;
    font-weight: 600;
    margin: 0;
    min-width: max-content;
  }
}

.date-range-selection {
  align-items: center;
  display: flex;
  gap: 8px;

  & > .date-picker > .mx-input-wrapper > input {
    margin-bottom: 0 !important;
  }
}

.grand-total-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.grand-total-label {
  font-weight: 600;
  color: var(--s-600);
  font-size: 14px;
}

.grand-total-value {
  font-weight: 700;
  color: var(--p-600);
  font-size: 14px;
}

.loadMore_btn button {
  display: block;
  margin: 16px auto;
}

.campaign_filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  .campaign_filter {
    p {
      margin-bottom: 8px;
    }
  }
}

.filters__button-wrap {
  position: relative;

  .filters__applied-indicator {
    background-color: var(--s-500);
    border-radius: var(--border-radius-rounded);
    height: var(--space-small);
    position: absolute;
    right: var(--space-slab);
    top: var(--space-smaller);
    width: var(--space-small);
  }
}
</style>
